import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { TokenRenewalCoordinatorService } from './token-renewal-coordinator.service';
import { SESSION_EXPIRY_WARNING_THRESHOLD_SECONDS } from '../../shared/constants';

/** State surfaced to the warning dialog. */
export interface SessionWarningState {
  /** Whether the "session expiring soon" dialog should be visible. */
  show: boolean;
  /** Seconds left before expiry (for the countdown). */
  secondsRemaining: number;
}

/**
 * How often we check the remaining token lifetime for the UI warning. A short
 * cadence keeps the countdown reasonably live once the warning is up without
 * being a busy loop.
 */
const CHECK_INTERVAL_MS = 5 * 1000;

/**
 * SYRC-6339 — drives the proactive pre-expiry warning.
 *
 * Polls the remaining id-token lifetime (via TokenRenewalCoordinatorService,
 * the single expiry source) and flips a "warn now" flag when it drops to/below
 * the warning threshold (120s). This sits BELOW the 300s renewal window, so
 * under normal operation the background renewal keeps the token fresh and this
 * warning never fires; it only surfaces when renewal has actually failed to
 * keep up — which is exactly when the user needs to act.
 *
 * This service does not renew or log out; it only reports state. The host
 * (AppComponent) wires the dialog's actions to renewNow() / the existing
 * expiry flow.
 */
@Injectable({ providedIn: 'root' })
export class SessionWarningService implements OnDestroy {
  private readonly warnThresholdSeconds =
    SESSION_EXPIRY_WARNING_THRESHOLD_SECONDS;

  private readonly state$ = new BehaviorSubject<SessionWarningState>({
    show: false,
    secondsRemaining: 0,
  });

  private intervalId: ReturnType<typeof setInterval> | null = null;
  /** Suppresses re-showing after the user dismisses, until the token renews. */
  private dismissed = false;

  constructor(
    private readonly tokenRenewalCoordinator: TokenRenewalCoordinatorService,
  ) {}

  /** Observable warning state for the host component to render. */
  get warning$(): Observable<SessionWarningState> {
    return this.state$.asObservable();
  }

  /** Begin polling (idempotent). Runs an immediate check. */
  start(): void {
    if (this.intervalId !== null) {
      return;
    }
    void this.check();
    this.intervalId = setInterval(() => void this.check(), CHECK_INTERVAL_MS);
  }

  /** Stop polling and hide the warning. Safe to call when not running. */
  stop(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.hide();
  }

  /**
   * User dismissed the warning: hide it and stay quiet until the token renews
   * (crosses back above the threshold), so we don't nag every few seconds.
   */
  acknowledge(): void {
    this.dismissed = true;
    this.hide();
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private async check(): Promise<void> {
    let secondsToExpiry: number | null;
    try {
      secondsToExpiry = await this.tokenRenewalCoordinator.secondsToExpiry();
    } catch {
      return; // transient read failure — leave current state untouched
    }

    if (secondsToExpiry === null) {
      // Logged out / no token — nothing to warn about.
      this.hide();
      this.dismissed = false;
      return;
    }

    if (secondsToExpiry > this.warnThresholdSeconds) {
      // Token healthy (renewal kept up). Reset so a later drop can warn again.
      this.dismissed = false;
      this.hide();
      return;
    }

    // Inside the warning window. Show unless the user already dismissed it.
    if (!this.dismissed) {
      this.state$.next({ show: true, secondsRemaining: secondsToExpiry });
    }
  }

  private hide(): void {
    if (this.state$.value.show) {
      this.state$.next({ show: false, secondsRemaining: 0 });
    }
  }
}
